import { Spinner } from '@brush/ui';
import { BreakdownEventCard, formatPoints, formatWhen, ordinal } from './BreakdownEventCard';
import { usePlayer } from '../player';
import { useLeaderboardBreakdown } from '../queries';

/**
 * §7.4, on the player's side of the glass: why this player has the points they have.
 *
 * Design goal 1 is an audit trail a player can read, and the sentence carrying it is the
 * server's — every row arrives with an `explanation` the engine wrote when it computed the
 * figures, and this component's job is to put it where somebody will read it rather than to
 * assemble one of its own. That is not tidiness. "I won and got third-place points" is the
 * support question §7.4 names, its answer depends on the deal rule the room's system happens to
 * configure, and a client wording it independently would be a fourth implementation of §3.4 that
 * nothing tests.
 *
 * So the explanation is the body of each card, at reading size, and the figures sit under it as
 * corroboration. The layout is deliberately the opposite of the designer's preview table, which
 * is scanned by somebody comparing hundreds of rows; this is read once, by one person, about
 * themselves.
 */
export function LeaderboardBreakdown({
  leaderboardId,
  playerId,
  playersRanked,
}: {
  leaderboardId: string;
  playerId: string;
  /**
   * Board size, for "3rd of 274". The breakdown is about one player and does not carry it.
   *
   * Optional because the fantasy screens open this under a *team's* member, where the board's size
   * is not on screen and is not what the reader is comparing against — they are looking at one of
   * their picks, not at a leaderboard. Omitted, the rank still reads, without an invented total.
   */
  playersRanked?: number;
}) {
  const { theme } = usePlayer();
  const query = useLeaderboardBreakdown(leaderboardId, playerId);

  if (query.isLoading) return <Spinner label="Working out the points…" />;

  if (query.isError || !query.data) {
    return (
      <p role="alert" className="px-5 py-4 text-sm" style={{ color: theme.textMuted }}>
        Couldn’t load the points breakdown just now.
      </p>
    );
  }

  const breakdown = query.data;
  const places = breakdown.decimalPlaces;

  // Two absences, two sentences. A board that has published nothing and a board this player is
  // not on look identical if both just say "no events" — and only one of them is worth checking
  // back on.
  if (breakdown.events.length === 0) {
    return (
      <p className="px-5 py-4 text-sm" style={{ color: theme.textMuted }}>
        {breakdown.sequenceNumber === null
          ? 'This leaderboard hasn’t published standings yet, so there’s nothing to break down.'
          : 'None of the events this leaderboard scores has a finish for this player.'}
      </p>
    );
  }

  return (
    <div className="space-y-3 px-5 pb-5 pt-1">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-bold">
          {/* "of N" is the board as it is on screen. Dropped rather than shown when a rank
              exceeds it — a player deleted since the snapshot was written still holds their row
              in it but not in the standings list, and "300th of 274" reads as a broken number
              where the rank on its own is simply true. */}
          {breakdown.rank === null
            ? 'Unranked'
            : playersRanked !== undefined && breakdown.rank <= playersRanked
              ? `${ordinal(breakdown.rank)} of ${playersRanked}`
              : ordinal(breakdown.rank)}
          {breakdown.isTied ? ' (tied)' : ''}
        </span>
        <span className="text-sm" style={{ color: theme.textMuted }}>
          {formatPoints(breakdown.totalPoints, places)} points from {breakdown.eventsScored} scored
          event{breakdown.eventsScored === 1 ? '' : 's'}
          {breakdown.events.length !== breakdown.eventsScored
            ? `, out of ${breakdown.events.length} entered`
            : ''}
        </span>
      </header>

      <ul className="space-y-2">
        {breakdown.events.map((event) => (
          <BreakdownEventCard key={event.eventId} event={event} places={places} />
        ))}
      </ul>

      {/* §8: these are a snapshot, not a live figure. A player who has just cashed and cannot
          find it here needs to know the board has not been recalculated yet, rather than
          concluding their result was lost. */}
      <p className="text-xs" style={{ color: theme.textMuted }}>
        Worked out when the room last updated this leaderboard
        {breakdown.computedAt ? `, ${formatWhen(breakdown.computedAt)}` : ''}
        {breakdown.isFinal ? '. These are the final standings.' : '.'}
      </p>
    </div>
  );
}
